import type { ActivationCodeView } from '@khaddi/contracts';
import { Button, colors, Field, Screen, Stack, Text, TopBar, useAction, useApi, useRouter, useSession } from '@khaddi/ui';
import { useEffect, useState } from 'react';
import { View } from 'react-native';
import { InlineError, Section, ShownCode } from '../components/bits';
import { useTabNav } from '../components/TabNav';

const SIX_DIGITS = /^\d{6}$/;
const digits = (t: string) => t.replace(/\D/g, '').slice(0, 6);
/** While the cashier holds the shown code, check every few seconds whether they've verified us. */
const POLL_MS = 4000;

/** Phone verification (needed to redeem rewards) and the first PIN for accounts made before PINs existed. */
export const VerifyScreen = () => {
  const { user } = useSession();
  const { back } = useRouter();
  const nav = useTabNav('account', { wideOnly: true });
  if (!user) return null;
  const done = user.phoneVerified && user.hasPin;

  return (
    <Screen nav={nav} maxWidth={720}>
      <TopBar title="Bảo mật tài khoản" onBack={() => back('/account')} />
      <View style={{ padding: 16, gap: 12 }}>
        {done ? (
          <Section>
            <Text variant="bodyStrong" color={colors.greenText}>
              Tài khoản đã xác minh và có mã PIN.
            </Text>
            <Text variant="caption">Bạn có thể đổi quà và đăng nhập bằng số điện thoại + PIN trên máy khác.</Text>
          </Section>
        ) : null}
        {!user.phoneVerified ? <VerifyPhone /> : null}
        {!user.hasPin ? <SetPin /> : null}
      </View>
    </Screen>
  );
};

const VerifyPhone = () => {
  const api = useApi();
  const { refreshUser } = useSession();
  const [code, setCode] = useState('');
  const [shown, setShown] = useState<(ActivationCodeView & { until: number }) | null>(null);
  const [now, setNow] = useState(Date.now());

  const verify = useAction(async () => {
    await api.auth.verifyWithCode(code);
    await refreshUser();
  });
  const show = useAction(async () => {
    const result = await api.auth.showStaffCode();
    setShown({ ...result, until: Date.now() + result.expiresInSeconds * 1000 });
  });

  const secondsLeft = shown ? Math.max(0, Math.round((shown.until - now) / 1000)) : 0;
  const counting = shown !== null && secondsLeft > 0;

  useEffect(() => {
    if (!counting) return;
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const poll = setInterval(() => void refreshUser().catch(() => {}), POLL_MS);
    return () => {
      clearInterval(tick);
      clearInterval(poll);
    };
  }, [counting, refreshUser]);

  return (
    <Section style={{ gap: 14 }}>
      <Stack gap={4}>
        <Text variant="bodyStrong">Xác minh số điện thoại</Text>
        <Text variant="caption">Cần xác minh để đổi quà. Điểm mua tại quầy vẫn được tích bình thường.</Text>
      </Stack>
      <Stack gap={10}>
        <Field
          label="Mã kích hoạt in trên hóa đơn"
          placeholder="6 số"
          keyboardType="number-pad"
          inputMode="numeric"
          maxLength={6}
          value={code}
          onChangeText={(t) => setCode(digits(t))}
          onSubmitEditing={() => SIX_DIGITS.test(code) && void verify.run()}
          style={{ letterSpacing: 4, fontWeight: '700' }}
        />
        <InlineError message={verify.error} />
        <Button label="Xác minh" onPress={() => void verify.run()} loading={verify.pending} disabled={!SIX_DIGITS.test(code)} />
      </Stack>
      <View style={{ height: 1, backgroundColor: colors.divider }} />
      <Stack gap={10}>
        <Text variant="caption">Đang ở quầy? Đưa mã dưới đây cho thu ngân để xác minh ngay.</Text>
        {shown && counting ? (
          <ShownCode code={shown.code} secondsLeft={secondsLeft} />
        ) : null}
        <InlineError message={show.error} />
        <Button label={shown ? 'Lấy mã mới' : 'Hiện mã cho thu ngân'} variant="outline" onPress={() => void show.run()} loading={show.pending} />
      </Stack>
    </Section>
  );
};

const SetPin = () => {
  const api = useApi();
  const { refreshUser } = useSession();
  const [pin, setPin] = useState('');
  const [pinAgain, setPinAgain] = useState('');
  const save = useAction(async () => {
    await api.auth.setPin(pin);
    await refreshUser();
  });
  const mismatch = pinAgain.length === 6 && pin !== pinAgain ? 'Hai lần nhập PIN chưa khớp' : null;

  return (
    <Section style={{ gap: 14 }}>
      <Stack gap={4}>
        <Text variant="bodyStrong">Tạo mã PIN</Text>
        <Text variant="caption">Lần sau đăng nhập bằng số điện thoại và PIN 6 số, không cần mã OTP.</Text>
      </Stack>
      <Field label="Mã PIN (6 số)" placeholder="••••••" keyboardType="number-pad" inputMode="numeric" secureTextEntry maxLength={6} value={pin} onChangeText={(t) => setPin(digits(t))} style={{ letterSpacing: 6, fontWeight: '700' }} />
      <Field label="Nhập lại mã PIN" placeholder="••••••" keyboardType="number-pad" inputMode="numeric" secureTextEntry maxLength={6} value={pinAgain} onChangeText={(t) => setPinAgain(digits(t))} error={mismatch} style={{ letterSpacing: 6, fontWeight: '700' }} />
      <InlineError message={save.error} />
      <Button label="Lưu mã PIN" onPress={() => void save.run()} loading={save.pending} disabled={!SIX_DIGITS.test(pin) || pin !== pinAgain} />
    </Section>
  );
};
